import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Removed React Hook Form and Zod imports
import { authAPI } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { FileText, Loader2, Eye, EyeOff, Globe } from 'lucide-react';

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrorKey, setLoginErrorKey] = useState(''); // Store the key for translation
  const [loginErrorMsg, setLoginErrorMsg] = useState(''); // Store raw message if no key
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const [rememberMe, setRememberMe] = useState(false);

  const validateForm = () => {
    const errors = { email: '', password: '' };
    let isValid = true;

    // Only check if email is provided
    if (!formData.email) {
      errors.email = 'E-Mail ist erforderlich';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (loginErrorKey || loginErrorMsg) {
      setLoginErrorKey('');
      setLoginErrorMsg('');
    }
  };

  const onSubmit = async (e) => {
    if (e) e.preventDefault();
    console.log('onSubmit called'); // Debug log
    try {
      setLoginErrorKey('');
      setLoginErrorMsg('');
      setFormErrors({ email: '', password: '' });

      console.log('Validating form...'); // Debug log
      if (!validateForm()) {
        console.log('Form validation failed'); // Debug log
        return;
      }

      console.log('Setting loading to true'); // Debug log
      setLoading(true);

      try {
        console.log('Saving remember me preference'); // Debug log
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        console.log('Making API call...'); // Debug log
        const response = await authAPI.login(formData);
        console.log('API call successful:', response); // Debug log

        console.log('Logging in user...'); // Debug log
        login(response.data.user, response.data.access_token);

        console.log('Showing success toast...'); // Debug log
        toast.success(t('auth.loginSuccess'));

        console.log('Navigating to home...'); // Debug log
        navigate('/');
      } catch (apiError) {
        console.error('API Error:', apiError); // Debug için

        if (apiError.response?.status === 401) {
          setLoginErrorKey('auth.invalidCredentials');
        } else {
          setLoginErrorMsg(apiError.response?.data?.detail || 'auth.loginError');
        }

        console.log('Error set'); // Debug log
      } finally {
        console.log('Setting loading to false'); // Debug log
        setLoading(false);
      }
    } catch (unexpectedError) {
      console.error('Unexpected error in onSubmit:', unexpectedError);
      setLoginErrorKey('auth.loginError');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f0f2f5] p-4 relative">
      <div className="absolute top-4 right-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-lg">
              <Globe className="w-5 h-5 text-gray-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            <DropdownMenuItem onClick={() => i18n.changeLanguage('tr')}>Türkçe</DropdownMenuItem>
            <DropdownMenuItem onClick={() => i18n.changeLanguage('en')}>English</DropdownMenuItem>
            <DropdownMenuItem onClick={() => i18n.changeLanguage('de')}>Deutsch</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Header Section */}
      <div className="flex flex-col items-center mb-4">
        <div className="w-56 h-56 flex items-center justify-center -mb-8">
          <img src="/logo.png" alt="Connected Mobile" className="w-full h-full object-contain" />
        </div>
        <h1 className="text-lg font-bold text-gray-900">{t('auth.brandName', { defaultValue: 'Contract Manager' })}</h1>
        <p className="text-gray-500 text-sm">www.connectedmobile.de</p>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-[400px] bg-white border-0 shadow-sm">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-2xl font-bold text-left">{t('auth.login')}</CardTitle>
          <CardDescription className="text-left text-gray-500">
            {t('auth.loginSubtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t('auth.email')}
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              {formErrors.email && (
                <p className="text-xs text-red-500">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {(loginErrorKey || loginErrorMsg) && (
                <p className="text-xs text-red-500">
                  {loginErrorKey ? t(loginErrorKey) : (loginErrorMsg === 'auth.loginError' ? t('auth.loginError') : loginErrorMsg)}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 text-black focus:ring-black h-4 w-4"
                />
                <label htmlFor="remember" className="text-xs text-gray-500 font-medium cursor-pointer" onClick={() => setRememberMe(!rememberMe)}>
                  {t('auth.rememberMe')}
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-xs font-bold text-gray-700 hover:text-black"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button
              type="button"
              onClick={() => {
                console.log('Button clicked'); // Debug log
                try {
                  onSubmit();
                } catch (error) {
                  console.error('Button click error:', error);
                }
              }}
              className="w-full bg-[#1a1a1a] hover:bg-black text-white font-bold h-11 mt-4"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.login')
              )}
            </Button>

          </form>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-8 text-center text-xs text-gray-300 font-medium">
        {t('auth.brandFooter', { defaultValue: 'Hand crafted & made by ZeuZ-IT' })}
      </div>
    </div>
  );
}
